import { Clock, Mail, MessageSquare, MessageSquareText, Pencil, Phone, Trash2 } from 'lucide-react'
import pluralize from 'pluralize'
import { type ReactNode } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { type SequenceStep, SequenceStepStatus, SequenceStepType } from '@/models/sequence'

const ICON = {
  [SequenceStepType.SEND_EMAIL]: Mail,
  [SequenceStepType.SEND_SMS]: MessageSquareText,
  [SequenceStepType.CALL]: Phone,
  [SequenceStepType.TEXT]: MessageSquare,
  [SequenceStepType.WAIT]: Clock,
}

// Card tint per step type, mirroring the Activity CTA button colors.
const STYLE = {
  [SequenceStepType.SEND_EMAIL]: { card: 'border-blue-200 bg-blue-50', icon: 'text-blue-600' },
  [SequenceStepType.SEND_SMS]: { card: 'border-violet-200 bg-violet-50', icon: 'text-violet-600' },
  [SequenceStepType.CALL]: { card: 'border-emerald-200 bg-emerald-50', icon: 'text-emerald-600' },
  [SequenceStepType.TEXT]: { card: 'border-sky-200 bg-sky-50', icon: 'text-sky-600' },
  [SequenceStepType.WAIT]: { card: 'border-neutral-200 bg-neutral-50', icon: 'text-neutral-500' },
}

export function summarize(step: SequenceStep): string {
  switch (step.stepType) {
    case SequenceStepType.SEND_EMAIL:
      return step.config.subject || '(no subject)'
    case SequenceStepType.SEND_SMS:
      return step.config.body?.trim() || '(no message)'
    case SequenceStepType.WAIT:
      return `Wait ${step.config.days ?? 0} ${pluralize('day', step.config.days ?? 0)}`
    case SequenceStepType.CALL:
      return 'Call lead'
    case SequenceStepType.TEXT:
      return 'Text lead'
    default:
      return ''
  }
}

interface SequenceStepCardProps {
  step: SequenceStep
  editable: boolean
  onEdit?: () => void
  onDelete?: () => void
  dragHandle?: ReactNode
}

export function SequenceStepCard({ step, editable, onEdit, onDelete, dragHandle }: SequenceStepCardProps) {
  const Icon = ICON[step.stepType]
  const style = STYLE[step.stepType]
  const done = step.status === SequenceStepStatus.COMPLETED
  return (
    <Card className={`gap-0 py-0 ${style.card} ${done ? 'opacity-60' : ''}`}>
      <CardContent className="flex items-center gap-2 px-3 py-1.5">
        {dragHandle}
        <Icon className={`size-4 shrink-0 ${style.icon}`} />
        <span className="flex-1 truncate text-sm">{summarize(step)}</span>
        {done ? (
          <Badge variant="secondary">Done</Badge>
        ) : (
          step.scheduledFor && <Badge variant="outline">{step.scheduledFor}</Badge>
        )}
        {editable && onEdit && (
          <Button variant="ghost" size="icon-sm" onClick={onEdit} aria-label="Edit step">
            <Pencil className="size-4" />
          </Button>
        )}
        {editable && onDelete && (
          <Button variant="ghost" size="icon-sm" onClick={onDelete} aria-label="Delete step">
            <Trash2 className="size-4" />
          </Button>
        )}
      </CardContent>
    </Card>
  )
}
